import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { retrieveFromLocalStorage, toBase64, getFileExtension, sortArray } from '../../../../utils/util';
import { NOT_SURE, ERROR } from '../../../../utils/constants';

import {
  requestViewAllCategories,
  requestViewAllClassifications,
  requestViewAllTypes,
  requestViewMetricUnits,
  requestViewAllMetricUnits,
} from '../../../../api/product/product.actions';
import {
  requestRetrieveBuyerListing,
  requestSendForAmendment,
  requestUpdateBuyerListing,
  requestApproveProduct,
} from '../../../../api/admin/product/admin-product.actions';
import { setBuyerListing, setCategories, setTypes } from '../../../../slices/product/product.slice';
import { addSystemNotice } from '../../../../slices/system/system.slice';

import BuyerListingManagement from '../../../../components/screens/admin/product-management/buyer-listing-management/BuyerListingManagement';

const BuyerListingManagementContainer = () => {
  const { classifications, categories, types, metricUnits, buyerListing } = useSelector(({ product }) => product);
  const currencyCode = useSelector(({ currency }) => currency.currencyCode);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { productExternalReference } = useParams();

  useEffect(() => {
    dispatch(requestViewAllClassifications());
  }, [dispatch]);

  useEffect(() => {
    if (productExternalReference) {
      dispatch(requestRetrieveBuyerListing({ productExternalReference }));
    }

    return () => dispatch(setBuyerListing(undefined));
  }, [dispatch, productExternalReference, currencyCode]);

  const retrieveCategories = useCallback(
    (classificationExternalReference) => {
      if (classificationExternalReference === NOT_SURE) {
        dispatch(setCategories([]));
        return;
      }

      dispatch(requestViewAllCategories({ classificationExternalReference }));
    },
    [dispatch]
  );

  const retrieveTypes = useCallback(
    (categoryExternalReference) => {
      if (categoryExternalReference === NOT_SURE) {
        dispatch(setTypes([]));
        return;
      }

      dispatch(requestViewAllTypes({ categoryExternalReference }));
    },
    [dispatch]
  );

  const retrieveMetricUnits = useCallback(
    (typeExternalReference) => {
      if (typeExternalReference === NOT_SURE) {
        dispatch(requestViewAllMetricUnits());
        return;
      }

      dispatch(requestViewMetricUnits({ typeExternalReference }));
    },
    [dispatch]
  );

  const navigateToAccount = (externalReference) => {
    navigate(`/admin/account-management/${externalReference}`);
  };

  const mapImages = async (images) => {
    const mappedImages = [];

    for (const image of images) {
      mappedImages.push({
        base64String: await toBase64(image),
        documentExtension: getFileExtension(image.name),
      });
    }

    return mappedImages;
  };

  const showErrorToast = (message) => {
    dispatch(addSystemNotice({ type: ERROR, message }));
  };

  const updateBuyerListing = async (buyerListing, showToaster, onSuccess) => {
    let newImages = buyerListing.photos.filter((photo) => typeof photo !== 'string');
    const oldImages = buyerListing.photos.filter((photo) => typeof photo === 'string');

    if (newImages.length > 0) {
      newImages = await mapImages(newImages);
      buyerListing.photos = newImages;
    } else {
      buyerListing.photos = [];
    }

    buyerListing.linkedMediaExternalReferences = oldImages;
    buyerListing.personExternalReference = retrieveFromLocalStorage('personExternalReference');
    buyerListing.productExternalReference = productExternalReference;

    dispatch(requestUpdateBuyerListing({ buyerListing, showToaster, onSuccess }));
  };

  const sendForAmendment = (comment, onSuccess, buyerListingBool) => {
    const amendment = {
      comments: comment,
      listingExternalReference: buyerListing.productExternalReference,
      personToNotifyExternalReference: buyerListing.uploadedBy.externalReference,
      buyerListing: buyerListingBool,
    };

    dispatch(requestSendForAmendment({ amendment, onSuccess }));
  };

  const approveProduct = () => {
    dispatch(
      requestApproveProduct({
        externalReference: buyerListing.productExternalReference,
        onSuccess: () => dispatch(requestRetrieveBuyerListing({ productExternalReference })),
      })
    );
  };

  return (
    <BuyerListingManagement
      listing={buyerListing}
      classifications={sortArray(classifications)}
      categories={sortArray(categories)}
      types={sortArray(types)}
      metricUnits={metricUnits}
      retrieveCategories={retrieveCategories}
      retrieveTypes={retrieveTypes}
      retrieveMetricUnits={retrieveMetricUnits}
      updateBuyerListing={updateBuyerListing}
      sendForAmendment={sendForAmendment}
      approveProduct={approveProduct}
      showErrorToast={showErrorToast}
      navigateToAccount={navigateToAccount}
      currencyCode={currencyCode}
    />
  );
};

export default BuyerListingManagementContainer;
